import React from 'react';

import { useThemeMode } from '@/nova/theme/OwsTheme';
import type { ResolvedMode } from '@/nova/theme/themeConfig';

import {
  BRAND_DEPLOYMENT_AURORA_STOPS,
  BRAND_DEPLOYMENT_AURORA_CFG,
  BRAND_DEPLOYMENT_AURORA_INK,
} from '../../../../../theme/tokens';
import { Canvas } from './AuroraField.styles';

/**
 * A flowing aurora rendered with a raw WebGL2 fragment shader (no GL library) —
 * a port of the reactbits Aurora: simplex-noise ribbon, a colour ramp across X,
 * with `amplitude` (height variance), `blend` (edge softness) and `speed` (flow)
 * controls. Sits behind the section's frosted glass.
 *
 * The ribbon's geometry and the brand colour stops are the same in both themes, but
 * the way it COMPOSITES follows the resolved theme, because "add light" and "add
 * pigment" are opposite operations:
 *  - dark: an additive glow over the near-black surface (the original model).
 *  - light: a subtractive "ink on paper" wash over the white surface, which keeps the
 *    same magenta/cyan stops but resolves them to pastels (BRAND_DEPLOYMENT_AURORA_INK).
 * Both branches live in the one program and are selected by the `uInk` uniform, so a
 * theme switch never leaves a stale frame.
 *
 * Lifecycle: compiles once, animates via rAF, and pauses when off-screen / the
 * tab is hidden / reduced-motion is requested (one static frame then idle). Falls
 * back to an empty (transparent) canvas if WebGL2 is unavailable — the card still
 * reads via its surface + glass.
 */
export type AuroraFieldProps = {
  /** Three colour stops (left → mid → right) parsed to RGB for the GL uniform. */
  colorStops?: readonly [string, string, string];
  amplitude?: number;
  blend?: number;
  speed?: number;
  /** When true the rAF loop never starts (one static frame). Reactive: a live OS
   *  toggle re-runs the effect, so the aurora pauses/resumes with the preference. */
  reduced?: boolean;
  /** Forces the composite model instead of following the resolved studio theme. Only
   *  the CT spec passes it, to render both models without standing up the theme
   *  provider; app consumers leave it off so the aurora tracks Settings. */
  mode?: ResolvedMode;
  className?: string;
};

const VERT = `#version 300 es
in vec2 position;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}`;

const FRAG = `#version 300 es
precision highp float;

uniform float uTime;
uniform float uAmplitude;
uniform vec3 uColorStops[3];
uniform vec2 uResolution;
uniform float uBlend;
// Composite model: 0 = additive glow (dark canvas), 1 = subtractive ink (white paper).
uniform float uInk;
uniform float uInkGain;
uniform float uInkTint;

out vec4 fragColor;

vec3 permute(vec3 x) {
  return mod(((x * 34.0) + 1.0) * x, 289.0);
}

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod(i, 289.0);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
  m = m * m;
  m = m * m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

struct ColorStop {
  vec3 color;
  float position;
};

#define COLOR_RAMP(colors, factor, finalColor) {                 \\
  int index = 0;                                                 \\
  for (int i = 0; i < 2; i++) {                                  \\
    ColorStop currentColor = colors[i];                          \\
    bool isInBetween = currentColor.position <= factor;          \\
    index = int(mix(float(index), float(i), float(isInBetween)));\\
  }                                                              \\
  ColorStop currentColor = colors[index];                        \\
  ColorStop nextColor = colors[index + 1];                       \\
  float range = nextColor.position - currentColor.position;      \\
  float lerpFactor = (factor - currentColor.position) / range;   \\
  finalColor = mix(currentColor.color, nextColor.color, clamp(lerpFactor, 0.0, 1.0)); \\
}

void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;

  ColorStop colors[3];
  colors[0] = ColorStop(uColorStops[0], 0.0);
  colors[1] = ColorStop(uColorStops[1], 0.5);
  colors[2] = ColorStop(uColorStops[2], 1.0);

  vec3 rampColor;
  COLOR_RAMP(colors, uv.x, rampColor);

  float height = snoise(vec2(uv.x * 2.0 + uTime * 0.1, uTime * 0.25)) * 0.5 * uAmplitude;
  height = exp(height);
  height = (uv.y * 2.0 - height + 0.2);
  float intensity = 0.6 * height;

  float midPoint = 0.20;
  float auroraAlpha = smoothstep(midPoint - uBlend * 0.5, midPoint + uBlend * 0.5, intensity);

  // DARK: an additive glow. Scaling the ramp BY the intensity means the ribbon adds
  // light to a near-black canvas and dims to nothing past its edge.
  vec3 auroraColor = intensity * rampColor;
  vec4 glow = vec4(auroraColor * auroraAlpha, auroraAlpha);

  // LIGHT: ink on paper. On white the maths above lands a heavy saturated band, so
  // here the hue is held CONSTANT and the intensity drives pigment density instead.
  // Source-over with a premultiplied alpha resolves to mix(paper, rampColor, inkAlpha),
  // so the paper takes at most uInkTint of the brand hue and stays paper elsewhere.
  float density = clamp(intensity * uInkGain, 0.0, 1.0) * auroraAlpha;
  float inkAlpha = density * uInkTint;
  vec4 ink = vec4(rampColor * inkAlpha, inkAlpha);

  fragColor = mix(glow, ink, uInk);
}`;

/** "#RRGGBB" / "#RGB" → [r, g, b] in 0..1. */
const hexToRgb = (hex: string): [number, number, number] => {
  const clean = hex.replace('#', '');
  const full = clean.length === 3 ? clean.replace(/(.)/g, '$1$1') : clean;
  const n = parseInt(full, 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
};

const compileShader = (gl: WebGL2RenderingContext, type: number, source: string): WebGLShader | null => {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
};

export const AuroraField = ({
  colorStops = BRAND_DEPLOYMENT_AURORA_STOPS,
  amplitude = BRAND_DEPLOYMENT_AURORA_CFG.amplitude,
  blend = BRAND_DEPLOYMENT_AURORA_CFG.blend,
  speed = BRAND_DEPLOYMENT_AURORA_CFG.speed,
  reduced = false,
  mode,
  className,
}: AuroraFieldProps): React.ReactElement => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const stopsKey = colorStops.join(',');
  // Subscribing here re-runs the effect on a live theme switch, so the composite model
  // follows Settings without a remount. Called unconditionally (hooks order), with the
  // optional prop taking precedence.
  const resolvedTheme = useThemeMode();
  const themeMode = mode ?? resolvedTheme;

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl2', { alpha: true, premultipliedAlpha: true, antialias: true });
    if (!gl) return; // Graceful fallback: no aurora, the glass + surface still read.

    const vert = compileShader(gl, gl.VERTEX_SHADER, VERT);
    const frag = compileShader(gl, gl.FRAGMENT_SHADER, FRAG);
    const program = gl.createProgram();
    if (!vert || !frag || !program) return;

    gl.attachShader(program, vert);
    gl.attachShader(program, frag);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;
    gl.useProgram(program);

    // Full-screen triangle (covers clip space, no index buffer needed).
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const posLoc = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); // premultiplied alpha

    const uTime = gl.getUniformLocation(program, 'uTime');
    const uAmplitude = gl.getUniformLocation(program, 'uAmplitude');
    const uBlend = gl.getUniformLocation(program, 'uBlend');
    const uResolution = gl.getUniformLocation(program, 'uResolution');
    const uColorStops = gl.getUniformLocation(program, 'uColorStops');
    const uInk = gl.getUniformLocation(program, 'uInk');
    const uInkGain = gl.getUniformLocation(program, 'uInkGain');
    const uInkTint = gl.getUniformLocation(program, 'uInkTint');

    const stops = new Float32Array(colorStops.flatMap((hex) => hexToRgb(hex)));
    gl.uniform3fv(uColorStops, stops);
    gl.uniform1f(uAmplitude, amplitude);
    gl.uniform1f(uBlend, blend);
    // Exactly 0 or 1: the shader's mix() then resolves to one model with no cross-fade.
    gl.uniform1f(uInk, themeMode === 'light' ? 1 : 0);
    gl.uniform1f(uInkGain, BRAND_DEPLOYMENT_AURORA_INK.gain);
    gl.uniform1f(uInkTint, BRAND_DEPLOYMENT_AURORA_INK.tint);

    let raf = 0;
    let running = false;
    // Timestamp of the most recent frame, so a resize can repaint the SAME moment.
    let lastTime = 0;

    const draw = (timeSeconds: number): void => {
      lastTime = timeSeconds;
      gl.uniform1f(uTime, timeSeconds * speed);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };

    const resize = (): void => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(canvas.clientWidth * dpr));
      const h = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
        gl.uniform2f(uResolution, w, h);
        // Sizing a canvas CLEARS its drawing buffer, and while the loop is paused
        // (reduced motion, scrolled off-screen, hidden tab) no frame would ever follow,
        // so the aurora would stay wiped. ResizeObserver always delivers an initial
        // notification too, which is enough to race the very first frame away.
        if (!running) draw(lastTime);
      }
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);

    const loop = (t: number): void => {
      draw(t / 1000);
      raf = requestAnimationFrame(loop);
    };
    const start = (): void => {
      if (running || reduced) return;
      running = true;
      raf = requestAnimationFrame(loop);
    };
    const stop = (): void => {
      running = false;
      cancelAnimationFrame(raf);
    };

    // Pause when scrolled out of view or the tab is hidden — no GPU churn off-screen.
    const visObserver = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        if (visible && !document.hidden) start();
        else stop();
      },
      { threshold: 0 },
    );
    visObserver.observe(canvas);

    const onVisibility = (): void => {
      if (document.hidden) stop();
      else start();
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Always paint at least one frame (covers reduced-motion + first paint).
    draw(0);

    return () => {
      stop();
      observer.disconnect();
      visObserver.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      gl.deleteBuffer(buffer);
      gl.deleteShader(vert);
      gl.deleteShader(frag);
      gl.deleteProgram(program);
    };
  }, [stopsKey, amplitude, blend, speed, colorStops, reduced, themeMode]);

  return <Canvas ref={canvasRef} className={className} aria-hidden="true" />;
};

export default AuroraField;
